import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { isServer } from 'shared/library/is-server';
import { ApiRoute } from 'shared/library/api-route';
import { AnyObject } from 'shared/@types';
import { env } from 'shared/library/env';
import { monitoring } from 'shared/library/monitoring';
import { LanguageCode, getSafeLanguageCode } from './i18n/languages';
import { platformOS } from './platform';

function buildApiBaseUrl(languageCode?: string | null): string {
  const safeLanguageCode = getSafeLanguageCode(languageCode);
  const apiUrl = env.API_URL.replace(/\/$/, '');

  return `${apiUrl}/${safeLanguageCode}`;
}

const defaultApiHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  'X-Platform': platformOS,
  'X-Server': isServer,
  'X-App-Version': env.APP_VERSION,
  'X-Media-Contract': 'r2-v1',
};

const serverApiTimeoutMs = 1000 * 2;
const browserApiTimeoutMs = 1000 * 30;
const defaultApiTimeoutMs = isServer ? serverApiTimeoutMs : browserApiTimeoutMs;
const serverGetRetryCount = 2;
const serverGetRetryDelayMs = [100, 300];
const serverGetBudgetMs =
  serverApiTimeoutMs * (serverGetRetryCount + 1) +
  serverGetRetryDelayMs.reduce((total, delay) => total + delay, 0);
const retriableServerStatusCodes = new Set([502, 503, 504]);

let initializedApiClient: AxiosInstance | undefined;

const apiClient = {
  async request<T = unknown>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    if (!initializedApiClient) {
      const { default: axios } = await import('axios');
      // Cache only the initialized client, never a request's pending promise.
      initializedApiClient ??= axios.create({
        baseURL: buildApiBaseUrl(),
        timeout: defaultApiTimeoutMs,
        headers: defaultApiHeaders,
      });
    }

    return initializedApiClient.request<T>(config);
  },
};

function getApiLanguageCode(languageCode?: string | null): LanguageCode {
  const safeLanguageCode = getSafeLanguageCode(languageCode);

  return safeLanguageCode;
}

type RequestClientContext = {
  languageCode?: string | null;
  apiKey?: string | null;
  headers?: AnyObject;
  logContext?: AnyObject;
  timeoutMs?: number;
  retryCount?: number;
};

type ApiRequestContext = {
  method: string;
  url: string;
  baseURL?: string;
  timeout: number;
  duration?: number;
  requestId: string;
  requestIds?: string[];
  attempts?: number;
  attemptDurations?: number[];
  status?: number;
  code?: string;
  message?: string;
  hasResponse?: boolean;
  ssrRay?: unknown;
  cfRay?: unknown;
  cfCacheStatus?: unknown;
  originRequestId?: unknown;
  params?: string[];
  body?: string[];
};

function getObjectKeys(value?: AnyObject): string[] {
  return value ? Object.keys(value).sort() : [];
}

function createRequestId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `req_${Date.now()}_${Math.random().toString(36).slice(2)}`
  );
}

function getMaxAttempts(method: string, retryCount = serverGetRetryCount): number {
  return isServer && method === 'get' ? Math.max(0, Math.floor(retryCount)) + 1 : 1;
}

function isRetriableApiError(error: any): boolean {
  if (error?.response) {
    return retriableServerStatusCodes.has(error.response.status);
  }

  if (error?.code === 'ERR_CANCELED') {
    return false;
  }

  return (
    ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error?.code) || Boolean(error?.request)
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeApiTagValue(value?: string): string {
  if (!value) {
    return 'unknown';
  }

  return value
    .replace(/^\/breeds\/[^/]+\/compare\/[^/?]+/, '/breeds/:breed/compare/:comparable')
    .replace(/^\/breeds\/[^/?]+/, '/breeds/:breed')
    .replace(/^\/breeders\/[^/?]+/, '/breeders/:breeder')
    .replace(/^\/listings\/[^/?]+/, '/listings/:listing')
    .replace(/^\/products\/[^/?]+/, '/products/:product')
    .replace(/\d+/g, ':id')
    .slice(0, 200);
}

function getHeaderValue(headers: any, key: string): unknown {
  if (!headers) {
    return undefined;
  }

  if (typeof headers.get === 'function') {
    return headers.get(key);
  }

  return headers[key] ?? headers[key.toLowerCase()];
}

function getApiRequestContext(context: ApiRequestContext): ApiRequestContext {
  const requestContext: ApiRequestContext = { ...context };

  if (!requestContext.params?.length) {
    delete requestContext.params;
  }

  if (!requestContext.body?.length) {
    delete requestContext.body;
  }

  return requestContext;
}

function logServerApiRequest(level: 'warn' | 'error', event: string, context: ApiRequestContext) {
  if (!isServer) {
    return;
  }

  const payload = JSON.stringify({
    source: 'ssr_api_client',
    event,
    ...context,
  });

  if (level === 'error') {
    console.error(payload);
    return;
  }

  console.warn(payload);
}

function createRequestClient(context: RequestClientContext = {}): RequestClient {
  return async <T = unknown>(
    route: ApiRoute,
    data?: AnyObject,
    headers?: AnyObject,
    abortController?: AbortController,
  ): Promise<T> => {
    const params = route.method === 'get' ? data : undefined;
    const body = route.method === 'get' ? undefined : data;
    const resolvedLanguageCode = getApiLanguageCode(context.languageCode);
    const providedRequestId = headers?.['X-Request-ID'] ?? context.headers?.['X-Request-ID'];
    const initialRequestId = providedRequestId ?? createRequestId();
    const baseHeaders = {
      ...context.headers,
      ...(context.apiKey ? { Authorization: `Bearer ${context.apiKey}` } : {}),
      ...headers,
    };
    const baseURL = buildApiBaseUrl(resolvedLanguageCode);
    const timeout = context.timeoutMs ?? defaultApiTimeoutMs;
    const startedAt = Date.now();
    const maxAttempts = getMaxAttempts(route.method, context.retryCount);
    const useDefaultSsrGetBudget =
      isServer &&
      route.method === 'get' &&
      context.timeoutMs === undefined &&
      context.retryCount === undefined;
    // A cold response can exceed two seconds; let it finish within the same total retry budget.
    const deadline = useDefaultSsrGetBudget ? startedAt + serverGetBudgetMs : undefined;
    let requestTimeout = useDefaultSsrGetBudget ? serverGetBudgetMs : timeout;
    const requestIds: string[] = [];
    const attemptDurations: number[] = [];
    const paramKeys = getObjectKeys(params);
    const bodyKeys = getObjectKeys(body);
    const logContext = context.logContext ?? {};

    monitoring.Sentry.addBreadcrumb({
      category: 'api',
      message: `${route.method.toUpperCase()} ${route.url}`,
      level: 'info',
      data: getApiRequestContext({
        method: route.method,
        url: route.url,
        timeout: requestTimeout,
        requestId: initialRequestId,
        ...logContext,
        params: paramKeys,
        body: bodyKeys,
      }),
    });

    let lastError: any;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const attemptStartedAt = Date.now();
      if (deadline !== undefined) {
        if (attempt > 1 && attemptStartedAt >= deadline) break;
        requestTimeout = Math.max(1, deadline - attemptStartedAt);
      }
      const requestId = attempt === 1 ? initialRequestId : createRequestId();
      requestIds.push(requestId);

      try {
        const response = await apiClient.request<T>({
          method: route.method,
          url: route.url,
          data: body,
          params,
          headers: {
            ...baseHeaders,
            'X-Request-ID': requestId,
          },
          signal: abortController?.signal,
          baseURL,
          timeout: requestTimeout,
        });

        return response.data;
      } catch (error: any) {
        lastError = error;
        attemptDurations.push(Date.now() - attemptStartedAt);

        if (
          attempt >= maxAttempts ||
          abortController?.signal.aborted ||
          !isRetriableApiError(error)
        ) {
          break;
        }

        const retryDelayMs = serverGetRetryDelayMs[attempt - 1] ?? 0;
        if (deadline !== undefined && Date.now() + retryDelayMs >= deadline) break;
        await sleep(retryDelayMs);
      }
    }

    const responseHeaders = lastError?.response?.headers;
    const apiRequest = getApiRequestContext({
      method: route.method,
      url: route.url,
      baseURL,
      timeout: requestTimeout,
      duration: Date.now() - startedAt,
      requestId: requestIds[0] ?? initialRequestId,
      requestIds,
      attempts: requestIds.length,
      attemptDurations,
      status: lastError?.response?.status,
      code: lastError?.code,
      message: lastError?.message,
      hasResponse: Boolean(lastError?.response),
      cfRay: getHeaderValue(responseHeaders, 'cf-ray'),
      cfCacheStatus: getHeaderValue(responseHeaders, 'cf-cache-status'),
      originRequestId: getHeaderValue(responseHeaders, 'x-request-id'),
      ...logContext,
      params: paramKeys,
      body: bodyKeys,
    });

    logServerApiRequest('error', 'ssr_api_failed', apiRequest);

    lastError.apiRequest = apiRequest;
    monitoring.Sentry.setTag('api.request_id', apiRequest.requestId);
    monitoring.Sentry.setTag('api.method', route.method);
    monitoring.Sentry.setTag('api.url', normalizeApiTagValue(route.url));
    monitoring.Sentry.setTag('api.code', lastError?.code ?? 'unknown');
    monitoring.Sentry.setTag('api.status', lastError?.response?.status ?? 'unknown');
    monitoring.Sentry.setContext('api_request', apiRequest);
    monitoring.Sentry.addBreadcrumb({
      category: 'api',
      message: `${route.method.toUpperCase()} ${route.url} failed`,
      level: 'error',
      data: apiRequest,
    });

    throw lastError;
  };
}

export type RequestClient = <T = unknown>(
  route: ApiRoute,
  data?: AnyObject,
  headers?: AnyObject,
  abortController?: AbortController,
) => Promise<T>;

const serverRequest = createRequestClient();
const createServerRequest = createRequestClient;

function parseArrayParam<T>(value?: T | null): T | null {
  if (!value) return null;

  if (Array.isArray(value)) return value;

  return (value as string)
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean) as T;
}

function coerceValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (trimmed === '') return '';

  if (!isNaN(+value)) return +value;
  if (value.toLowerCase() === 'true') return true;
  if (value.toLowerCase() === 'false') return false;

  return value;
}

function sanitizeQueryParams<T extends AnyObject>(
  values: Partial<T>,
  defaults: T,
  arrayParams: (keyof T)[] = [],
): T {
  const merged: AnyObject = { ...defaults, ...values };

  for (const key of Object.keys(defaults)) {
    const raw = merged[key];

    if (arrayParams.includes(key as keyof T)) {
      const parsed = parseArrayParam(raw);
      merged[key] = Array.isArray(parsed) ? parsed.map(coerceValue) : parsed;
    } else {
      merged[key] = coerceValue(raw);
    }
  }

  return merged as T;
}

function prepareDataForApi<T extends AnyObject>(
  values: T,
  arrayParams: (keyof T)[] = [],
): AnyObject {
  if (Array.isArray(values)) {
    return values.map((v) => prepareDataForApi(v));
  }

  if (values !== null && typeof values === 'object') {
    return Object.fromEntries(
      Object.entries(values).map(([key, val]) => {
        const snakeKey = toSnakeCase(key);

        if (arrayParams.includes(key)) {
          return [snakeKey, parseArrayParam(val)];
        }

        return [snakeKey, prepareDataForApi(val, arrayParams)];
      }),
    );
  }

  return values;
}

function toSnakeCase(str: string): string {
  return str
    .replace(/([A-Z])/g, '_$1')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();
}

export {
  apiClient,
  createRequestClient,
  createServerRequest,
  serverRequest,
  getApiLanguageCode,
  parseArrayParam,
  sanitizeQueryParams,
  prepareDataForApi,
};
